import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2, CheckCircle2, XCircle, MessageSquare, Mail } from "lucide-react";
import { toast } from "sonner";
import { collectTapToPay, cancelTapToPay } from "../../utils/tapToPay";
import { TapToPayGlyph, ttpLabel } from "./TapToPayButton";

const money = (v) => `$${(parseFloat(v) || 0).toFixed(2)}`;

/** Shared Tap to Pay collection dialog (owner + technician).
 *  `api` = { createIntent(amount, reqId), finalize(piId), cancel(piId),
 *            tokenEndpoint, authToken(), sendReceipt?(paymentId, via) } */
export default function TapToPayDialog({ open, onClose, doc, api, onPaid, initialAmount }) {
  const total = doc?.total || 0;
  const paid = doc?.amount_paid || 0;
  const balance = Math.max(total - paid, 0);

  const [amount, setAmount] = useState("");
  const [step, setStep] = useState("amount"); // amount | tapping | verifying | success | failed
  const [tapMsg, setTapMsg] = useState("");
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(null);
  const intentRef = useRef(null);
  const reqIdRef = useRef(null);

  useEffect(() => {
    if (open) {
      setAmount(initialAmount != null && initialAmount > 0 ? Number(initialAmount).toFixed(2)
        : balance > 0 ? balance.toFixed(2) : "");
      setStep("amount");
      setError("");
      setResult(null);
      intentRef.current = null;
      reqIdRef.current = `${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.invoice_id]);

  if (!doc) return null;
  const amt = parseFloat(amount) || 0;
  const willFullyPay = amt >= balance - 0.005 && balance > 0;

  const finalizeWithRetry = async (piId) => {
    for (let i = 0; i < 5; i++) {
      const fin = await api.finalize(piId);
      if (fin.success) return fin;
      if (fin.payment_status !== "processing") return fin;
      await new Promise((r) => setTimeout(r, 2500));
    }
    return { success: false, payment_status: "processing",
             message: "Payment is still processing — the invoice will update automatically once Stripe confirms it." };
  };

  const startTap = async () => {
    if (amt < 0.5) { toast.error("Minimum card payment is $0.50"); return; }
    if (amt > balance + 0.005) { toast.error(`Amount exceeds the remaining balance (${money(balance)})`); return; }
    setError("");
    setTapMsg("Preparing payment…");
    setProgress(null);
    setStep("tapping");
    try {
      if (!intentRef.current || intentRef.current.amount !== amt) {
        intentRef.current = await api.createIntent(amt, reqIdRef.current);
      }
      const intent = intentRef.current;
      const res = await collectTapToPay({
        payment_intent_id: intent.payment_intent_id,
        client_secret: intent.client_secret,
        location_id: intent.location_id,
        connected_account_id: intent.connected_account_id,
        merchant_display_name: intent.merchant_display_name,
        amount: amt,
        currency: intent.currency || "usd",
        display_name: `${doc.invoice_number || ""} ${doc.customer_name || ""}`.trim(),
        token_endpoint: api.tokenEndpoint,
        auth_token: api.authToken(),
        tos_allowed: api.tosAllowed !== false,
      }, { onStatus: (m, _ph, p) => { setTapMsg(m || ""); setProgress(p ?? null); } });

      if (res.canceled) {
        setStep("amount");
        return;
      }
      setStep("verifying");
      // ALWAYS verify with the server — the client result alone never marks a payment paid.
      const fin = await finalizeWithRetry(intent.payment_intent_id);
      if (fin.success) {
        setResult(fin);
        setStep("success");
        onPaid?.(fin);
      } else {
        setError(res.error || fin.message || "Payment was not completed");
        setStep("failed");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (e.message || "Payment failed"));
      intentRef.current = null; // intent creation may have failed — start clean
      setStep("failed");
    }
  };

  const cancelCollect = async () => {
    cancelTapToPay();
    const pi = intentRef.current?.payment_intent_id;
    if (pi) api.cancel(pi).catch(() => {});
    intentRef.current = null;
    setStep("amount");
  };

  const sendReceipt = async (via) => {
    if (!api.sendReceipt || !result?.payment_id || busy) return;
    setBusy(via);
    try {
      await api.sendReceipt(result.payment_id, via);
      toast.success("Receipt sent");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send receipt");
    } finally { setBusy(null); }
  };

  const sendDecline = async (via) => {
    const pi = intentRef.current?.payment_intent_id;
    if (!api.sendDeclineReceipt || !pi || busy) return;
    setBusy(via);
    try {
      await api.sendDeclineReceipt(pi, via);
      toast.success("Decline notice sent");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send notice");
    } finally { setBusy(null); }
  };

  const closeGuarded = () => {
    if (step === "tapping" || step === "verifying") return; // no accidental dismiss mid-payment
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={closeGuarded}>
      <DialogContent className="sm:max-w-md" data-testid="tap-to-pay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TapToPayGlyph className="w-5 h-5 text-sky-500" /> {ttpLabel()}
          </DialogTitle>
          <DialogDescription>
            {doc.invoice_number} · {doc.customer_name} · Balance {money(balance)}
          </DialogDescription>
        </DialogHeader>

        {step === "amount" && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Amount to collect</label>
              <input
                type="number" step="0.01" min="0" inputMode="decimal"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); intentRef.current = null; }}
                className="w-full h-11 rounded-xl px-3 text-lg font-bold border border-border bg-transparent text-foreground"
                data-testid="ttp-amount-input"
              />
              <div className="flex gap-1.5">
                <button type="button" onClick={() => { setAmount((balance / 2).toFixed(2)); intentRef.current = null; }}
                  className="px-2 py-1 rounded-md text-[11px] font-medium bg-blue-500/10 text-blue-500" data-testid="ttp-pct-50">50%</button>
                <button type="button" onClick={() => { setAmount(balance.toFixed(2)); intentRef.current = null; }}
                  className="px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-500/10 text-emerald-500" data-testid="ttp-pct-full">Full Balance</button>
              </div>
              {amt > 0 && !willFullyPay && (
                <p className="text-[11px] text-amber-500">Partial payment — {money(Math.max(balance - amt, 0))} will remain</p>
              )}
              {willFullyPay && <p className="text-[11px] text-emerald-500">This pays the invoice in full</p>}
            </div>
            <Button onClick={startTap} disabled={amt <= 0}
              className="w-full h-12 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-semibold"
              data-testid="ttp-start-btn">
              <TapToPayGlyph className="w-5 h-5 mr-2" /> {ttpLabel()} · {money(amt)}
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">
              The customer taps their card, phone, or watch on the back of this device. Processed securely by Stripe.
            </p>
          </div>
        )}

        {step === "tapping" && (
          <div className="flex flex-col items-center gap-3 py-6" data-testid="ttp-tapping">
            <div className="w-16 h-16 rounded-full bg-sky-500/15 flex items-center justify-center">
              <TapToPayGlyph className="w-9 h-9 text-sky-500 animate-pulse" />
            </div>
            <p className="text-lg font-bold text-foreground">{money(amt)}</p>
            <p className="text-sm text-muted-foreground text-center" data-testid="ttp-status-msg">
              {tapMsg || "Ask the customer to tap their card or phone…"}
            </p>
            {progress != null && (
              <div className="w-full max-w-[220px]" data-testid="ttp-config-progress">
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-sky-500 transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
                </div>
                <p className="text-[10px] text-muted-foreground text-center mt-1">Configuring Tap to Pay… {Math.min(progress, 100)}%</p>
              </div>
            )}
            <Button variant="outline" onClick={cancelCollect} data-testid="ttp-cancel-btn">Cancel</Button>
          </div>
        )}

        {step === "verifying" && (
          <div className="flex flex-col items-center gap-3 py-8" data-testid="ttp-verifying">
            <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
            <p className="text-sm text-muted-foreground">Confirming the payment with Stripe…</p>
          </div>
        )}

        {step === "success" && result && (
          <div className="space-y-4 text-center" data-testid="ttp-success">
            <div className="flex flex-col items-center gap-2 py-2">
              <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
              <p className="text-2xl font-bold text-foreground">{money(result.amount || amt)}</p>
              <p className="text-xs text-muted-foreground">
                {result.fully_paid ? "Invoice fully paid" : `Remaining balance ${money(result.balance_due)}`}
              </p>
            </div>
            {api.sendReceipt && result.payment_id && (
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" disabled={!!busy} onClick={() => sendReceipt("sms")} data-testid="ttp-receipt-sms">
                  {busy === "sms" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MessageSquare className="w-4 h-4 mr-1" />} SMS Receipt
                </Button>
                <Button variant="outline" disabled={!!busy} onClick={() => sendReceipt("email")} data-testid="ttp-receipt-email">
                  {busy === "email" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />} Email Receipt
                </Button>
              </div>
            )}
            <Button onClick={onClose} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ttp-done-btn">Done</Button>
          </div>
        )}

        {step === "failed" && (
          <div className="space-y-4 text-center" data-testid="ttp-failed">
            <div className="flex flex-col items-center gap-2 py-2">
              <div className="w-14 h-14 rounded-full bg-red-500/15 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <p className="text-sm text-foreground">{error || "Payment was not completed"}</p>
              <p className="text-[11px] text-muted-foreground">No charge was recorded. You can safely try again.</p>
            </div>
            {api.sendDeclineReceipt && intentRef.current?.payment_intent_id && (
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" disabled={!!busy} onClick={() => sendDecline("sms")} data-testid="ttp-decline-sms">
                  {busy === "sms" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MessageSquare className="w-4 h-4 mr-1" />} Text outcome
                </Button>
                <Button variant="outline" size="sm" disabled={!!busy} onClick={() => sendDecline("email")} data-testid="ttp-decline-email">
                  {busy === "email" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />} Email outcome
                </Button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => setStep("amount")} data-testid="ttp-back-btn">Change amount</Button>
              <Button onClick={startTap} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="ttp-retry-btn">Try again</Button>
            </div>
            {intentRef.current && (
              <p className="text-[9px] leading-relaxed text-muted-foreground/70 font-mono break-all pt-1" data-testid="ttp-diag">
                {new URL(process.env.REACT_APP_BACKEND_URL).host}
                {" · "}{intentRef.current.key_mode || "?"} mode
                {" · acct …"}{(intentRef.current.connected_account_id || "").slice(-6)}
                {" · "}{intentRef.current.merchant_display_name || "—"}
                {" · "}{intentRef.current.backend_build || "?"}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
